import React from "react";
import { action, toJS } from "mobx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconName, SizeProp, library, findIconDefinition, IconPrefix, IconPack } from "@fortawesome/fontawesome-svg-core";
import { fas } from "@fortawesome/free-solid-svg-icons";
import { far } from "@fortawesome/free-regular-svg-icons";
import { fab } from "@fortawesome/free-brands-svg-icons";

import dayjs from "dayjs";
import numeral from "numeral";
import _ from "lodash";
import { common } from "./common";
import { Badge, Modal } from "antd";

import duration from "dayjs/plugin/duration";
dayjs.extend(duration);

library.add(fab as IconPack, fas, far);

export const stripLeading = (url: string) => url.replace(/^\.?\/?/, "");

export const addTrailing = (url: string) => {
  if (url[0] !== "/") url = "/" + url;
  if (url[url.length - 1] !== "/") url = url + "/";
  return url;
};

/**
 * ------------- IMPORTANT ---------------
 * ** DO NOT TOUCH WITHOUT SPEAK TO IFC **
 * This function is mutating the objects
 * @note Do not assign this func to the object as there is no return value
 * @warning Do not change this function without consulting IFC
 */
export const safeAssign = action((originalObj: any, sourceObj: any) => {
  // Get the keys from the original object for later comparison
  let keys = _.keys(originalObj);

  if (keys.length > 0) {
    // Check which keys are not used in the source object so we could remove them from
    // the original object to not have them after the assigning
    keys = keys.filter((val: any) => !_.hasIn(sourceObj, val));

    // Remove any unused key values from the original object
    keys.forEach((k: any) => {
      _.unset(originalObj, k);
    });
  }

  // Assign the values to the original object from the source
  _.assign(originalObj, sourceObj);
});

/**
 * ------------- IMPORTANT ---------------
 * ** DO NOT TOUCH WITHOUT SPEAK TO IFC **
 * This function will remove any other keys you don't need apart from ones passed into the keys defintion.
 * @warning Do not change this function without consulting IFC
 */
export const pruneObject = action((object: any, keys: any) => {
  // Return if there is no object or keys passed.
  if (!object) {
    return;
  }
  if (!keys) {
    return;
  }
  // Get the keys.
  const objectKeys = Object.keys(object);
  // Remove the keys that aren't needed.
  objectKeys.forEach((key, index) => {
    if (!keys.includes(key)) {
      delete object[key];
    }
  });
});

export const formatDuration = (duration: any, format = "HH:mm:ss", time: any = "milliseconds") => {
  return dayjs.duration(duration, time)["format"](format);
};

export const dutyEndTime = (startTime: any, endTime: any) => {
  const dutyStartTime = dayjs.utc(startTime);
  const depotReturn = dayjs.utc(endTime);

  const dutyDelta = depotReturn.diff(dutyStartTime, "days");

  if (dutyDelta > 0) {
    return depotReturn.format("HH:mm") + " +" + dutyDelta;
  }

  return depotReturn.format("HH:mm");
};

/**
 * Centralised logic to process the API.
 * Author : IFC
 */
export const extractErrorMessage = (err: any) => {
  // This is a check to see if the error was a message returned from the API.
  let errorMessage = _.get(err, "response.data.message", null);

  // Check if there was an API side error message.
  if (errorMessage) {
    // The backend can return an object or a string.
    if (typeof errorMessage === "string") {
      errorMessage = "API ERROR - " + errorMessage.toUpperCase();
    }
    // Or they can return an object in the case of a mongo error with an object wrapped inside it.
    else {
      // We try to extract it.
      errorMessage = _.get(errorMessage, "message", null);
      // If we find a value we format it correctly.
      if (errorMessage) {
        errorMessage = "API ERROR - " + errorMessage.toUpperCase();
      }
    }
  }

  // Check different types of messages.
  if (!errorMessage && err.message) {
    // Check if the error message is an axios error (network related).
    if (err.isAxiosError && typeof err.message === "string") {
      errorMessage = "API ERROR - " + err.message.toUpperCase();
    }
    // Otherwise check if the message is a string and dipslay it. This will be a UI issue.
    else if (typeof err.message === "string") {
      errorMessage = "UI ERROR - " + err.message.toUpperCase();
      console.log("UI Detailed Error Message : ", err.message);
    }
    // Last option is to log the error message as is of a different type that is not supported.
    else {
      console.log("Error Message : ", toJS(err));
    }
  }

  return errorMessage;
};

export const getTimeFromHours = (time: number, seconds = false): string => {
  let dutyHours = dayjs.duration(time, "hours");
  let hours = Math.floor(dutyHours.asHours());
  let mins = Math.floor(dutyHours.asMinutes()) - hours * 60;

  if (seconds) {
    let secs = Math.floor(dutyHours.asSeconds()) - hours * 60 * 60 - mins * 60;
    return (hours > 9 ? hours : "0" + hours) + ":" + (mins > 9 ? mins : "0" + mins) + ":" + (secs > 9 ? secs : "0" + secs);
  }

  return (hours > 9 ? hours : "0" + hours) + ":" + (mins > 9 ? mins : "0" + mins);
};

export const getLocaleMetric = (type = "distance", locale?: any) => {
  // Prepare variable.
  let metric = null;

  // Set defaults.
  switch (type) {
    case "distance":
      metric = "km";
      break;
    case "cost":
      metric = "euro";
      break;
    case "weight":
      metric = "kg";
      break;
    default:
      metric = null;
      break;
  }

  // Get the locale setting.
  if (locale) {
    metric = locale[type];
  }

  return metric;
};

export const computeDistance = (distance: any, locale: any) => {
  // Prepare variable.
  let computedDistance = distance;
  // Prepare variable.
  let metric = getLocaleMetric("distance", locale);

  if (metric === "mi") {
    // Convert to miles.
    computedDistance = distance * 0.621371;
  } else {
    // Convert to km.
    computedDistance = distance;
  }

  return numeral(computedDistance).format("0.00");
};

export const compareStrings = (compareTo: string, value: string) => {
  // Parse both strings
  const compareToParsed = compareTo.toLowerCase().replace(/\s/g, "");
  const valueParsed = value.toLowerCase().replace(/\s/g, "");

  return compareToParsed.includes(valueParsed);
};

export const copyTextToClipboard = (value: any) => {
  const el = document.createElement("textarea");
  el.value = value;
  document.body.appendChild(el);
  el.select();
  document.execCommand("copy");
  document.body.removeChild(el);
};

export const getCountryFlag = (countryCode: any) => {
  const code = countryCode.toLowerCase();

  return require("../data/countryFlags/" + code + ".svg");
};

export const deviceTimestampCorrection = (date: any, timezone = "Europe/London") => {
  const offset = dayjs(date).tz(timezone).utcOffset();

  return dayjs.utc(date).add(offset, "minutes");
};

export const awesomeIcon = (iconName: IconName, iconSize: SizeProp = "1x", style = {}, type: "regular" | "solid" | "brand" = "solid") => {
  let iconType: IconPrefix = "fas";

  switch (type) {
    case "regular":
      iconType = "far";
      break;
    case "solid":
      iconType = "fas";
      break;
    case "brand":
      iconType = "fab";
      break;
    default:
      iconType = "fas";
      break;
  }

  const icon = findIconDefinition({ prefix: iconType, iconName: iconName });

  // If the icon is not found return null to prevent the error as some icons might be paid
  if (!icon) {
    // console.log(`%cIcon ${iconName} not found`, 'background: orange; padding: 5px; color: #fff')
    return null;
  }

  return <FontAwesomeIcon style={{ ...style }} icon={{ prefix: iconType, iconName: iconName }} size={iconSize} />;
};

export const parseAddress = (data: any) => {
  if (data) {
    const { address1, address2, address3, address4, postCode, houseNumber, houseName, companyName, countryCode } = data;

    let fullAddress = "";

    fullAddress += companyName ? companyName.length > 0 && companyName + ", " : "";
    fullAddress += houseNumber ? houseNumber.length > 0 && houseNumber + ", " : "";
    fullAddress += houseName ? houseName.length > 0 && houseName + ", " : "";
    fullAddress += address1 ? address1.length > 0 && address1 + ", " : "";
    fullAddress += address2 ? address2.length > 0 && address2 + ", " : "";
    fullAddress += address3 ? address3.length > 0 && address3 + ", " : "";
    fullAddress += address4 ? address4.length > 0 && address4 + ", " : "";
    fullAddress += postCode ? postCode.length > 0 && postCode + ", " : "";
    fullAddress += countryCode ? countryCode.length > 0 && countryCode : "";

    return fullAddress;
  }

  return "";
};

export const getCookieValue = (item: string, value: string, defaultValue = null) => {
  const cookie = JSON.parse(localStorage.getItem(item));

  if (cookie) {
    return _.get(cookie, value, defaultValue);
  }

  return defaultValue;
};

export const setCookieValue = (key: string, value: any) => {
  const cookie = JSON.stringify(value);

  if (cookie) {
    return localStorage.setItem(key, cookie);
  }
};

// Return random number between nim and max values
export const rand = (min: number, max: number) => {
  return Math.floor(Math.random() * (max - min) + min);
};

export const validatePhoneNumber = (number: string) => {
  const check = /^[0-9-+\s]+$/g;

  return check.test(number);
};

export const validateEmailAddress = (email: string) => {
  const regex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

  return regex.test(email);
};

export const downloadExcelExport = (blobData: any, fileName: string, hasExtension = false) => {
  const blob = common.b64toBlob(blobData);
  const blobUrl = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.download = hasExtension ? fileName : fileName + ".xlsx";
  link.href = blobUrl;
  link.click();
};

// Modal to show
export const apiResponseModal = (apiResponse: any) => {
  Modal.warning({
    title: apiResponse.responseMessage,
    centered: true,
    okText: "Close",
    content: (
      <div className="flex flex-col">
        <div>
          <Badge color="blue" />
          Total - {apiResponse.nTotal}
        </div>
        <div>
          <Badge status="success" />
          Removed - {apiResponse.nRemoved}
        </div>
        <div>
          <Badge status="error" />
          Failed - {apiResponse.nNotRemoved}
        </div>
      </div>
    ),
  });
};

export const mongoId = (m = Math, d = Date, h = 16, s = (s: any) => m.floor(s).toString(h)) => {
  return s(d.now() / 1000) + " ".repeat(h).replace(/./g, () => s(m.random() * h));
};

// Validate if the coordinates are within the range
export const isValidCoordinates = (coordinates: Array<number>) => {
  const lat = Math.abs(_.get(coordinates, "[1]", 0));
  const lng = Math.abs(_.get(coordinates, "[0]", 0));

  if (lat <= 90 && lat >= -90 && lng <= 180 && lng >= -180) {
    return true;
  }

  return false;
};

/**
 * msg => notification Message
 * persist => to keep the notification showing
 * key => notification identification
 * downloadProgress => progress bar percentage
 * status => if its true it means error occurred during the process
 */
// export const displayDownloadNotification = (msg: string, persist: any, key, downloadProgress, status = null) => {
//   if (status) {
//     notification.close(key)
//   } else {
//     let msgNotify = ''
//     if (downloadProgress !== 100) {
//       msgNotify = 'Downloading ' + msg
//     } else {
//       msgNotify = 'Successfully Downloaded ' + msg
//     }

//     notification.open({
//       key,
//       message: msgNotify,
//       icon: <Icon type='download' />,
//       description: <Progress percent={downloadProgress} status='active' />,
//       duration: persist ? 0 : 1
//     })
//   }
// }

export const geoCodeToAddress = (place: any) => {
  const address: any = {};
  const OFAddress: any = {
    address: {
      address1: "",
      address2: "",
      address3: "",
      address4: "",
      countryCode: "",
      countryName: "",
      houseName: "",
      houseNumber: "",
      locality: "",
      postCode: "",
      region: "",
    },
    location: {
      coordinates: [],
      type: "Point",
    },
  };

  if (_.get(place, "geometry.location")) {
    OFAddress.location.coordinates = [place.geometry.location.lng(), place.geometry.location.lat()];
  }

  if (_.get(place, "address_components")) {
    place.address_components.forEach((p: any) => {
      if (p.types.includes("postal_code")) {
        address.postCode = p.short_name;
      }
      if (p.types.includes("premise")) {
        address.houseName = p.short_name;
      }
      if (p.types.includes("street_number")) {
        address.houseNumber = p.short_name;
      }
      if (p.types.includes("route")) {
        address.address1 = p.short_name;
      }
      if (p.types.includes("locality")) {
        address.address2 = p.short_name;
      }
      if (p.types.includes("postal_town")) {
        address.address3 = p.short_name;
      }
      if (p.types.includes("administrative_area_level_1")) {
        address.region = p.short_name;
      }
      if (p.types.includes("country")) {
        address.countryName = p.long_name;
        address.countryCode = p.short_name;
      }
    });
  }

  Object.keys(address).forEach((key) => {
    if (_.has(address, key)) {
      OFAddress.address[key] = address[key];
    } else {
      OFAddress.address[key] = "";
    }
  });

  return OFAddress;
};

// export const dayOfWeeksData = (dow = null) => {
//   const dowData = dayjs.weekdays().map((wd: any, i: any) => {
//     return { _id: i.toString(), value: i, name: wd };
//   });

//   if (dow !== null) {
//     return dowData[dow].name;
//   }

//   return dowData;
// };

export const exportJson = (data: any, fileName: string) => {
  const a = document.createElement("a");
  const file = new Blob([JSON.stringify(data, null, 4)], { type: "application/json" });
  a.href = URL.createObjectURL(file);
  a.download = fileName;
  a.click();
};

export const getOS = () => {
  let userAgent = window.navigator.userAgent,
    platform = window.navigator.platform,
    macosPlatforms = ["Macintosh", "MacIntel", "MacPPC", "Mac68K"],
    windowsPlatforms = ["Win32", "Win64", "Windows", "WinCE"],
    iosPlatforms = ["iPhone", "iPad", "iPod"],
    os = null;
  if (macosPlatforms.indexOf(platform) !== -1) {
    os = "macOS";
  } else if (iosPlatforms.indexOf(platform) !== -1) {
    os = "iOS";
  } else if (windowsPlatforms.indexOf(platform) !== -1) {
    os = "Windows";
  } else if (/Android/.test(userAgent)) {
    os = "Android";
  } else if (!os && /Linux/.test(platform)) {
    os = "Linux";
  }
  return os;
};

export const generateAddressString = (address: any) => {
  let addressString = "";

  if (address.houseNumber) {
    addressString += address.houseNumber + ", ";
  }
  if (address.houseName) {
    addressString += address.houseName + ", ";
  }
  if (address.address1) {
    addressString += address.address1 + ", ";
  }
  if (address.address2) {
    addressString += address.address2 + ", ";
  }
  if (address.address3) {
    addressString += address.address3 + ", ";
  }
  if (address.address4) {
    addressString += address.address4 + ", ";
  }
  if (address.postCode) {
    addressString += address.postCode + ", ";
  }
  if (address.countryCode) {
    addressString += address.countryCode;
  }

  return addressString;
};

export const dangerouslySetInnerHTML = (data: any) => {
  return <div dangerouslySetInnerHTML={{ __html: data }} />;
};

/**
 * Iterate object to find a matching case and return matching objects
 * targetData - Is an object
 * condition - is a function to find the matching object
 */
export const findObjects = (targetData: any, condition: Function, results: any = []) => {
  // Check if the targetData is not null because null in js is === to object (don't ask)
  if (targetData && typeof targetData === "object") {
    // Iterate object values
    Object.values(targetData).forEach((data: any) => {
      // If the data is an object or an array, we call itself to iterate again
      if (data && typeof data === "object") {
        // If its not an array and its an object, check the condition
        if (!Array.isArray(data)) {
          const isConditionTrue = condition(data);

          if (isConditionTrue) {
            results.push(data);
          }
        }

        findObjects(data, condition, results);
      }
    });
  }

  return results;
};

export const alphanumericCheck = (value: any, min = 3, max = 10) => {
  const regex = new RegExp(`^[a-zA-Z0-9-_]{${min},${max}}$`, "gi");

  // Returns false if it fails
  return regex.test(value);
};

// Download/export file which is converted as blob
// eg: new Blob([csv], { type: 'text/csv' })
export const downloadBlob = (blob: any, fileName: string) => {
  const csvUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.download = fileName;
  link.href = csvUrl;
  link.click();
};

export const checkPageAction = () => {
  // Get the action parameter
  // An example url http://localhost:3000/route?action=debugLink&options=depotId=58664e8b8c9ee231e0bf2a37
  const params = new URLSearchParams(window.location.search);
  const action = params.get("action");
  const options = params.get("options");
  const payload = {
    action: action,
    options: {},
  };

  if (action) {
    // Replace url to remove action parameter to prevent executing action on page refresh
    window.history.replaceState({}, document.title, window.location.href.split("?action")[0]);

    if (options) {
      options.split(",").forEach((option) => {
        const parsedOption = option.split("=");

        payload.options[parsedOption[0]] = parsedOption[1];
      });
    }

    // Pass the action back to the caller
    return payload;
  }
};

// generates a random number, with given integer digits and float digits
export const generateRandomNumber = (intDigits, floatDigits) => {
  if (intDigits < 0 || floatDigits < 0) {
    throw new Error("Invalid number of digits. Must be greater or equal to 0");
  }

  const minInt = 10 ** (intDigits - 1);
  const maxInt = 10 ** intDigits - 1;

  const minFloat = 10 ** -floatDigits;
  const maxFloat = 1 - 10 ** -floatDigits;

  const randomIntPart = Math.floor(minInt + Math.random() * (maxInt - minInt + 1));
  const randomFloatPart = parseFloat((Math.random() * (maxFloat - minFloat) + minFloat).toFixed(floatDigits));

  return parseFloat((randomIntPart + randomFloatPart).toFixed(floatDigits));
};

// Generates a random clusterRef based on internally provided specs
export const generateClusterRef = () => {
  const minLength = 3;
  const maxLength = 20;
  const alphanumericChars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";

  const randomLength = Math.floor(Math.random() * (maxLength - minLength + 1)) + minLength;
  let clusterRef = "";

  for (let i = 0; i < randomLength; i++) {
    const randomIndex = Math.floor(Math.random() * alphanumericChars.length);
    clusterRef += alphanumericChars.charAt(randomIndex);
  }

  return clusterRef;
};

// Returns a random element of input array
export const getRandomArrayElement = (arr) => {
  if (!Array.isArray(arr)) {
    throw new Error("Input is not an array");
  }
  if (arr.length === 0) {
    throw new Error("Array is empty");
  }

  const randomIndex = Math.floor(Math.random() * arr.length);

  return arr[randomIndex];
};

/**
 * Returns a random element from the array, but with a given percentage chance to prefer the first element.
 * @param arr The array to pick from
 * @param firstElementChance A number between 0 and 1 representing the chance to pick the first element (e.g. 0.7 for 70%)
 */
export const getRandomArrayElementPreferFirst = (arr, firstElementChance = 0.7) => {
  if (!arr || arr.length === 0) return undefined;
  if (Math.random() < firstElementChance) {
    return arr[0];
  }
  // Pick a random element from the rest (excluding the first)
  const idx = Math.floor(Math.random() * (arr.length - 1)) + 1;
  return arr[idx];
};

// Generates random coordinates with center and radius env vars
export const generateRandomCoordinates = (center, radiusInMeters) => {
  try {
    const metersPerDegreeLatitude = 111319.9;
    const latitudeChange = radiusInMeters / metersPerDegreeLatitude;

    const longitudeChange = radiusInMeters / (metersPerDegreeLatitude * Math.cos((center[1] * Math.PI) / 180));

    const randomLongitude = center[0] + (Math.random() - 0.5) * 2 * longitudeChange;
    const randomLatitude = center[1] + (Math.random() - 0.5) * 2 * latitudeChange;

    return [randomLongitude, randomLatitude];
  } catch (error) {
    console.error("An error occurred: ", error.message);
  }
};

// Generates shared location object
export const generateSharedLocationValue = function (key, countryCode, center, radiusInMeters) {
  let randomNumber = Math.floor(Math.random() * 100000);
  switch (key) {
    case "knownLocationRef":
      return "KLIntg" + generateRandomNumber(9, 0);
    case "companyName":
      return "Company " + randomNumber;
    case "houseNumber":
      return randomNumber.toString();
    case "houseName":
      return randomNumber + " House";
    case "address1":
      return "Street " + randomNumber;
    case "address2":
      return "Village " + randomNumber;
    case "address3":
      return "County " + randomNumber;
    case "address4":
      return "City " + randomNumber;
    case "postCode":
      return "ZIP " + randomNumber;
    case "countryCode":
      return countryCode;
    case "location":
      return {
        coordinates: generateRandomCoordinates(center, radiusInMeters),
        type: "Point",
      };
    default:
      return "UnknownValue" + randomNumber;
  }
};
